import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { StocktakingItem, StocktakingItemForm, StocktakingItemSearchForm } from 'entities/stocktaking_item'

type StocktakingItemsResponse = PagingResponse & {
    results: StocktakingItem[]
}

export function useFetchStocktakingItemsApi(searchForm?: Form<StocktakingItemSearchForm>): IndexApiSet<StocktakingItemsResponse> & { execute: () => void } {
    const apiPath = 'stocktaking_items/'
    const api = useIndexApi<StocktakingItemsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchStocktakingItemApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `stocktaking_items/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostStocktakingItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<StocktakingItemForm>) => void } {
    const apiSet = usePostApi<BaseResponse, StocktakingItemForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StocktakingItemForm>) => {
        const apiPath = `stocktaking_items/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutStocktakingItemApi(): ApiSet<BaseResponse> & { execute: (form: StocktakingItemForm) => void } {
    const apiSet = usePutApi<BaseResponse, StocktakingItemForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: StocktakingItemForm) => {
        const apiPath = `stocktaking_items/${form.id}/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutStocktakingItemsApi(): ApiSet<BaseResponse> & { execute: (form: Form<StocktakingItemForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, StocktakingItemForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StocktakingItemForm[]>) => {
        const apiPath = `stocktaking_items/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteStocktakingItemApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `stocktaking_items/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

type BoxesResponse = BaseResponse & {
    boxes: string[]
}
export function useFetchStocktakingBoxesApi(searchForm?: Form<StocktakingItemSearchForm>): ApiSet<BoxesResponse> & { execute: () => void } {
    const apiSet = useShowApi<BoxesResponse>(new CraifHttpClient(), {
        initialResponse: { boxes: [] },
    })

    const execute = () => {
        const apiPath = `stocktaking_items/boxes/`
        apiSet.execute(apiPath, searchForm?.object)
    }

    return { ...apiSet, execute: execute }
}
